import { useEffect, useState } from "react";
import { Bell, AlertTriangle, Package, Activity, CreditCard, Ship, FileText } from "lucide-react";
import api from "@/lib/api";
import { money, StatusBadge } from "@/components/admin/AdminLayout";

export function Notifications() {
  const [notes, setNotes] = useState([]);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/admin/notifications").then((r) => setNotes(r.data));
    api.get("/admin/events").then((r) => setEvents(r.data));
  }, []);
  return (
    <div className="space-y-5">
      <div><h1 className="font-display text-2xl font-bold tracking-tight">Notifications</h1><p className="text-sm text-zinc-500">Alerts & system event log</p></div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2"><Bell size={15} />Alerts</h3>
          {notes.length === 0 ? <p className="text-sm text-zinc-400">No alerts. Everything looks good.</p> : (
            <div className="space-y-2">
              {notes.map((n, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-lg bg-zinc-50" data-testid={`notification-${i}`}>
                  {n.type === "order" ? <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" /> : <Package size={16} className="text-amber-500 shrink-0 mt-0.5" />}
                  <div><p className="text-sm font-medium">{n.message}</p>{n.detail && <p className="text-xs text-zinc-500">{n.detail}</p>}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2"><Activity size={15} />Event Log</h3>
          <div className="space-y-1.5 max-h-96 overflow-auto">
            {events.map((e) => (
              <div key={e.id} className="flex justify-between text-xs py-1.5 border-b border-zinc-50 last:border-0">
                <span className="font-mono text-blue-600">{e.event_type}</span>
                <span className="text-zinc-400">{new Date(e.created_at).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Payments() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);
  const paid = orders.filter((o) => o.payment_status === "paid");
  const totalFees = paid.reduce((s, o) => s + (o.payment_fee || 0), 0);
  return (
    <div className="space-y-5">
      <div><h1 className="font-display text-2xl font-bold tracking-tight">Payments</h1><p className="text-sm text-zinc-500">Stripe · test mode sandbox</p></div>
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">Payments Collected</p><p className="text-2xl font-bold mt-1">{money(paid.reduce((s, o) => s + o.total, 0))}</p></div>
        <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">Transactions</p><p className="text-2xl font-bold mt-1">{paid.length}</p></div>
        <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">Processing Fees</p><p className="text-2xl font-bold mt-1 text-amber-600">{money(totalFees)}</p></div>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <CreditCard size={18} className="text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">Running on a Stripe claimable sandbox. Go live anytime from the Payments tab in Emergent — no code changes needed. The gateway architecture also supports adding PayPal, Paystack & Flutterwave.</p>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-zinc-500 bg-zinc-50 border-b"><th className="p-3 font-medium">Order</th><th className="p-3 font-medium">Amount</th><th className="p-3 font-medium">Fee</th><th className="p-3 font-medium">Status</th></tr></thead>
          <tbody>{paid.map((o) => <tr key={o.id} className="border-b border-zinc-50"><td className="p-3 font-mono text-xs">{o.order_number}</td><td className="p-3 font-medium">{money(o.total)}</td><td className="p-3 text-zinc-500">{money(o.payment_fee)}</td><td className="p-3"><StatusBadge status="Paid" /></td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

export function Shipping() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);
  const shipped = orders.filter((o) => ["Shipped", "In Transit", "Delivered"].includes(o.status));
  return (
    <div className="space-y-5">
      <div><h1 className="font-display text-2xl font-bold tracking-tight">Shipping</h1><p className="text-sm text-zinc-500">Shipments & delivery tracking</p></div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <Ship size={18} className="text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">Free shipping over $50, otherwise $4.99 flat. Supplier shipping options (CJPacket / Express) and per-country rates are fetched via each supplier adapter's calculateShipping().</p>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-zinc-500 bg-zinc-50 border-b"><th className="p-3 font-medium">Order</th><th className="p-3 font-medium">Destination</th><th className="p-3 font-medium">Method</th><th className="p-3 font-medium">Status</th></tr></thead>
          <tbody>{shipped.map((o) => <tr key={o.id} className="border-b border-zinc-50"><td className="p-3 font-mono text-xs">{o.order_number}</td><td className="p-3">{o.shipping_address?.city}, {o.shipping_address?.country}</td><td className="p-3 text-zinc-500">{o.shipping_method}</td><td className="p-3"><StatusBadge status={o.status} /></td></tr>)}</tbody>
        </table>
        {shipped.length === 0 && <p className="text-center py-12 text-zinc-400 text-sm">No shipments yet.</p>}
      </div>
    </div>
  );
}

export function Reports() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="animate-pulse h-64 bg-zinc-100 rounded-xl" />;
  const t = d.totals;
  return (
    <div className="space-y-5 max-w-3xl">
      <div><h1 className="font-display text-2xl font-bold tracking-tight">Reports</h1><p className="text-sm text-zinc-500">Financial summary</p></div>
      <div className="bg-white border border-zinc-200 rounded-xl p-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2"><FileText size={15} />Profit & Loss Summary</h3>
        <div className="space-y-2 text-sm">
          {[["Gross Revenue", money(t.revenue)], ["Supplier Costs", `-${money(t.supplier_costs)}`], ["Supplier Shipping", `-${money(t.shipping_costs)}`], ["Payment Fees", `-${money(t.payment_fees)}`]].map(([l, v]) => (
            <div key={l} className="flex justify-between py-1.5 border-b border-zinc-50"><span className="text-zinc-500">{l}</span><span className="font-medium">{v}</span></div>
          ))}
          <div className="flex justify-between py-2 text-base font-bold"><span>Net Estimated Profit</span><span className="text-green-600">{money(t.profit)}</span></div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">Total Orders</p><p className="text-xl font-bold mt-1">{t.orders}</p></div>
        <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">Avg Order Value</p><p className="text-xl font-bold mt-1">{money(t.aov)}</p></div>
        <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">Refunds</p><p className="text-xl font-bold mt-1">{t.refunds}</p></div>
      </div>
    </div>
  );
}
